<template>
  <v-card>
    <!--- Add payment method modal --->
    <Modal
      width="448"
      v-model="displayAddPaymentMethodModal"
      :title="$t('settings.billing.billing_method.enter_card_information')"
      :cta-text="$t('settings.billing.billing_method.add_card')"
      :cta-loading="addPaymentMethodLoading"
      :dialog-icon="'creditCard'"
      actions-border
      @confirm="submitAddPaymentMethodForm"
      @cancel="closeAddPaymentMethodModal">
      <StripeForm
        class="mt-5"
        :type="'paymentMethod'"
        @submit="handleAddPaymentMethodFormSubmit"
        ref="addPaymentMethodForm"
        :key="addPaymentMethodFormKey"
        :client-secret="clientSecret"></StripeForm>
    </Modal>
    <!-- Primary card change confirm modal --->
    <Modal
      width="400"
      v-model="displayPrimaryCardChangeModal"
      :title="$t('settings.billing.billing_method.change_primary_card')"
      :subtitle="$t('settings.billing.billing_method.change_card_disclaimer')"
      :cta-text="$t('settings.billing.billing_method.change_card_save')"
      :cta-loading="changePaymentMethodLoading"
      @confirm="changePaymentMethod"
      @cancel="cancelChangePaymentMethod">
    </Modal>
    <!-- Retry payment modal --->
    <Modal
      width="400"
      v-model="displayRetryPaymentModal"
      :title="$t('settings.billing.billing_method.retry_payment_modal_title')"
      :subtitle="
        $t('settings.billing.billing_method.retry_payment_modal_subtitle', {
          endNumbers: currentPaymentMethodData.last4,
        })
      "
      :cta-text="$t('settings.billing.billing_method.retry_payment_modal_cta_text')"
      :cta-loading="retryPaymentLoading"
      @confirm="retryPayment"
      @cancel="displayRetryPaymentModal = false">
    </Modal>
    <v-skeleton-loader
      v-if="paymentMethodsLoading"
      class="skeleton-loader"
      type="list-item-two-line, list-item-avatar, list-item-avatar"></v-skeleton-loader>

    <template v-else>
      <div class="d-flex">
        <div class="d-flex align-center">
          <div class="d-flex flex-column">
            <v-card-title>{{ $t("settings.billing.billing_method.billing_method_title") }}</v-card-title>
            <v-card-subtitle>{{ $t("settings.billing.billing_method.billing_method_subtitle") }}</v-card-subtitle>
          </div>
        </div>
        <v-card-actions class="ml-auto">
          <v-btn
            v-if="addPaymentBtnVisible"
            @click="handleAddPaymentModalOpen"
            :loading="createSetupIntentLoading"
            :class="{ 'add-payment-btn left-icon': !createSetupIntentLoading }"
            outlined
            :color="'primary'">
            <plus-icon class="fill--primary"></plus-icon>
            {{ $t("settings.billing.billing_method.add_payment_method") }}
          </v-btn>
        </v-card-actions>
      </div>

      <v-radio-group
        @change="displayChangePaymentModal"
        v-model="currentPaymentMethod"
        :hide-details="'auto'"
        class="payments-radio-group mt-0"
        row>
        <div
          v-for="paymentMethod in displayedPaymentMethods"
          :key="paymentMethod.id"
          class="payment-method d-flex align-center w-full">
          <v-radio class="payment-method__radio-box" :value="paymentMethod.id"></v-radio>
          <v-icon v-if="paymentMethod.type === 'card'" class="payment-method__icon">mdi-credit-card-outline</v-icon>
          <img v-else :src="PayPal" class="payment-method__icon" alt="PayPal" />
          <span class="payment-method__last-digits">{{ paymentMethod.display }}</span>
          <v-chip v-if="paymentMethod.default" class="payment-method__chip" :color="'info-120'" :text-color="'info'">
            {{ $t("settings.billing.billing_method.primary_payment_method") }}
          </v-chip>
          <DataListActions
            v-if="!paymentMethod.default"
            :actions="[
              {
                title: 'Delete',
                icon: 'mdi-trash-can-outline',
                function: deletePaymentMethod.bind(this, paymentMethod),
              },
            ]"></DataListActions>
        </div>
      </v-radio-group>
      <VaDisclaimer
        v-if="displayRetryPaymentBtn"
        :class="'spacing-top--16'"
        :type="'error'"
        :title="$t('settings.billing.billing_method.retry_payment_disclaimer_title')"
        :text="$t('settings.billing.billing_method.retry_payment_disclaimer_subtitle')"
        :icon-color="'error'">
        <template #actions>
          <v-btn @click="displayRetryPaymentModal = true" color="body" outlined>
            {{ $t("settings.billing.billing_method.retry_payment") }}
          </v-btn>
        </template>
      </VaDisclaimer>
    </template>
  </v-card>
</template>

<script>
  // Components
  import DataListActions from "@/components/custom/DataListActions.vue";
  import Modal from "@/components/custom/Modal";
  import StripeForm from "@/components/forms/StripeForm.vue";
  import VaDisclaimer from "@/components/custom/VaDisclaimer";
  // icons
  import PlusIcon from "@/components/icons/PlusIcon.vue";
  import PayPal from "@/assets/images/misc/paypal-blue.png";
  // Store
  import { mapGetters } from "vuex";
  // Utility
  import { formErrorMessage } from "@/utility/formErrorMessage";

  export default {
    name: "BillingMethod",
    components: {
      PlusIcon,
      DataListActions,
      Modal,
      StripeForm,
      VaDisclaimer,
    },
    data() {
      return {
        displayedPaymentMethods: [],
        displayAddPaymentMethodModal: false,
        displayPrimaryCardChangeModal: false,
        changePaymentMethodLoading: false,
        addPaymentMethodLoading: false,
        displayRetryPaymentModal: false,
        retryPaymentLoading: false,
        createSetupIntentLoading: false,
        clientSecret: "",
        addPaymentMethodFormKey: 0,
        currentPaymentMethod: null,
        oldPaymentMethod: null,
        PayPal,
      };
    },
    computed: {
      // Current payment method's full object
      currentPaymentMethodData() {
        return this.displayedPaymentMethods.find(paymentMethod => paymentMethod.id === this.currentPaymentMethod) || {};
      },
      // There should be a limit of maximum 3 cards per organization.
      addPaymentBtnVisible() {
        return this.displayedPaymentMethods.length < 3;
      },
      ...mapGetters({
        displayRetryPaymentBtn: "billing/displayRetryPaymentBtn",
        paymentMethods: "billing/getPaymentMethods",
        paymentMethodsLoading: "billing/getPaymentMethodsLoading",
        subscription: "billing/getSubscription",
      }),
    },
    watch: {
      paymentMethods: {
        immediate: true,
        handler(data) {
          if (!data.length) return;
          this.currentPaymentMethod = data.find(paymentMethod => paymentMethod.default)?.id;
          this.oldPaymentMethod = this.currentPaymentMethod;
          this.displayedPaymentMethods = [...data];
        },
      },
    },
    methods: {
      async handleAddPaymentModalOpen() {
        // We have to set clientSecret before rendering stripe form;
        const { clientSecret } = await this.createSetupIntent();
        this.clientSecret = clientSecret;
        this.displayAddPaymentMethodModal = true;
      },
      async createSetupIntent() {
        this.createSetupIntentLoading = true;
        try {
          const { data } = await this.$admin.dataProvider.customHttpCall(
            "POST",
            `subscriptions/${this.subscription.id}/payment_methods`,
          );

          return data;
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.createSetupIntentLoading = false;
        }
      },
      closeAddPaymentMethodModal() {
        this.displayAddPaymentMethodModal = false;
        // Reset form data;
        this.addPaymentMethodFormKey++;
        this.addPaymentMethodLoading = false;
      },
      submitAddPaymentMethodForm() {
        this.$refs.addPaymentMethodForm.submit();
      },
      async handleAddPaymentMethodFormSubmit(stripeData) {
        this.addPaymentMethodLoading = true;

        const { stripe, elements } = stripeData;

        const result = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/complete-billing-method?subscription=${this.subscription.id}`,
          },
          redirect: "if_required",
        });

        if (result.error) {
          this.$admin.toast.error(result.error.message || this.$t("error"));
          this.closeAddPaymentMethodModal();
          this.addPaymentMethodLoading = false;
        } else {
          const setupIntentId = result.setupIntent.id;
          try {
            const newPaymentMethod = await this.confirmNewPaymentMethod(setupIntentId);
            // Update payment methods list;
            this.displayedPaymentMethods.push(newPaymentMethod);
            this.$admin.toast.success(this.$t("settings.billing.billing_method.add_payment_method_success"));
          } catch (e) {
            this.$admin.toast.error(formErrorMessage(e, this.$t("error")));
          } finally {
            this.closeAddPaymentMethodModal();
            this.addPaymentMethodLoading = false;
          }
        }
      },
      async confirmNewPaymentMethod(setupIntentId) {
        try {
          const { data } = await this.$admin.dataProvider.customHttpCall(
            "POST",
            `subscriptions/${this.subscription.id}/payment_methods/confirm`,
            {
              setupIntentId,
            },
          );

          return data;
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        }
      },
      async changePaymentMethod() {
        this.changePaymentMethodLoading = true;
        try {
          const { data: newDefaultPaymentMethod } = await this.$admin.dataProvider.customHttpCall(
            "POST",
            `payment_methods/${this.currentPaymentMethod}/default`,
          );

          // Update payment methods list;
          this.displayedPaymentMethods = this.displayedPaymentMethods
            .map(paymentMethod => {
              return {
                ...paymentMethod,
                default: paymentMethod.id === newDefaultPaymentMethod.id,
              };
            })
            // Primary payment method comes first;
            .sort((a, b) => b.default - a.default);

          this.currentPaymentMethod = newDefaultPaymentMethod.id;
          this.oldPaymentMethod = this.currentPaymentMethod;

          this.displayPrimaryCardChangeModal = false;
          this.$admin.toast.success(this.$t("settings.billing.billing_method.primary_card_changed_successfully"));
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
          this.currentPaymentMethod = this.oldPaymentMethod;
        } finally {
          this.displayPrimaryCardChangeModal = false;
          this.changePaymentMethodLoading = false;
        }
      },
      displayChangePaymentModal() {
        this.displayPrimaryCardChangeModal = true;
      },
      cancelChangePaymentMethod() {
        this.currentPaymentMethod = this.oldPaymentMethod;
        this.displayPrimaryCardChangeModal = false;
      },
      async deletePaymentMethod(paymentMethod) {
        if (
          await this.$admin.confirm(
            this.$t("settings.billing.billing_method.delete_payment_card_title"),
            this.$t("settings.billing.billing_method.delete_payment_card_disclaimer"),
          )
        ) {
          try {
            await this.$admin.dataProvider.customHttpCall("DELETE", `payment_methods/${paymentMethod.id}`);
            // Update payment methods list;
            this.displayedPaymentMethods = this.displayedPaymentMethods.filter(
              method => method.id !== paymentMethod.id,
            );
            this.$admin.toast.success(this.$t("settings.billing.billing_method.delete_payment_card_success"));
          } catch (e) {
            this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
          }
        }
      },

      async retryPayment() {
        this.retryPaymentLoading = true;
        try {
          const stripe = await this.$store.dispatch("billing/getStripe");
          const { data } = await this.$admin.dataProvider.customHttpCall(
            "POST",
            `subscriptions/${this.subscription.id}/retry_payment`,
          );
          const { clientSecret } = data;

          // Make payment intent confirm request to Stripe
          const result = await stripe.confirmPayment({
            clientSecret,
            confirmParams: {
              return_url: "https://example.com/order/123/complete",
            },
            redirect: "if_required",
          });

          if (result.error) {
            this.$admin.toast.error(result.error.message || this.$t("generic.error"));
            this.retryPaymentLoading = false;
            this.displayRetryPaymentModal = false;
          } else {
            try {
              const subscriptionData = await this.refreshSubscription();
              this.$store.commit("billing/SET_SUBSCRIPTION", {
                ...this.$store.getters["billing/getSubscription"],
                ...subscriptionData,
              });
              this.$admin.toast.success(this.$t("settings.billing.billing_method.payment_successful"));
            } catch (e) {
              this.$admin.toast.error(this.$t("settings.billing.billing_method.payment_error"));
            } finally {
              this.retryPaymentLoading = false;
              this.displayRetryPaymentModal = false;
            }
          }
        } catch (e) {
          this.$admin.toast.error(this.$t("settings.billing.billing_method.payment_error"));
        } finally {
          this.retryPaymentLoading = false;
          this.displayRetryPaymentModal = false;
        }
      },
      async refreshSubscription() {
        try {
          const { data } = await this.$admin.dataProvider.customHttpCall(
            "POST",
            `subscriptions/${this.subscription.id}/refresh`,
          );

          return data;
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        }
      },
    },
  };
</script>

<style scoped lang="scss">
  @import "../../../../@core/preset/preset/typography/index";
  @import "../../../../@core/preset/preset/functions.scss";

  .add-payment-btn {
    &::v-deep {
      svg {
        margin-right: 9px;
      }
    }
  }
  .payments-radio-group {
    border-radius: 4px;
    border: 1px solid get-color("border");
    padding: 4px 0;
  }
  .payment-method {
    background-color: get-color("white");
    height: 60px;
    padding: 18px 24px;
    &:not(:last-of-type) {
      border-bottom: 1px solid get-color("border");
    }
    &__icon {
      margin-right: 12px;
    }
    &__last-digits {
      @include text-subtitle-2();
      color: get-color("headline");
      margin-right: 12px;
    }
    &__chip {
      height: 24px !important;
    }
    &__radio-box {
      margin-right: 14px;
      &::v-deep {
        .v-input--selection-controls__input {
          margin-right: 0;
        }
      }
    }
    &::v-deep {
      .data-list--action-btn {
        margin-left: auto;
        path {
          fill: get-color("icon");
        }
      }
    }
  }
</style>
